import { DateTime } from "@bounce/date"
import { formatStripeAmount, intlNumberFormat, revertStripeAmount } from "@bounce/i18n"
import { getAddress, getGmbAddress, getGoogleMapsUrl } from "@bounce/store"
import type { Nullable } from "@bounce/util"

import type { NearbyStorageSpot, StoreData } from "@/libs/bounce/stores/types"

export type BaseStoreInterpolations = {
  CITY_NAME: string
  COUNTRY_NAME: string
  FEATURED_POI_NAME: string
  FIRST_NEARBY_POI: string
  INSURANCE_AMOUNT: string
  LOWEST_PRICE_AMOUNT: string
  LOWEST_PRICE: string
  PRICE_AMOUNT: string
  PRICE_CURRENCY: string
  PRICE: string
  SECOND_NEARBY_POI: string
  STORE_ADDRESS: string
  STORE_GMB_NAME: string
  STORE_GOOGLE_MAPS_URL: string
  STORE_LIVE_DATE: string
  STORE_LUGGAGE_STORAGE_CAPACITY: string
  STORE_NAME: string
  STORE_TYPE: string
  YEAR: string
}

type GetBaseStoreInterpolationsOptions = {
  store: StoreData | NearbyStorageSpot
  pois: Nullable<{ translatedName: string }[]>
  language: string
  bounceGlobals: {
    bounceShieldProtection: string
  }
  displayRegionName: {
    of: (country: string) => string | undefined
  }
  formatCapacity: (value: number) => string
  storeType?: string
  unknownCapacityText?: string
}

export const getBaseStoreInterpolations = ({
  store,
  pois,
  language,
  bounceGlobals,
  displayRegionName,
  formatCapacity,
  storeType,
  unknownCapacityText = "unlimited",
}: GetBaseStoreInterpolationsOptions): BaseStoreInterpolations => {
  const city = store.city
  const country = displayRegionName.of(city.country) || city.country
  const priceInMainUnit = revertStripeAmount(store.price)
  const currency = city.currency

  const decimalPlaces = Number.isInteger(priceInMainUnit) ? 0 : 2
  const { format: formatDecimals } = intlNumberFormat({
    locale: language,
    options: {
      style: "decimal",
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    },
  })

  const price = formatStripeAmount(store.price, currency, language)
  const priceAmount = formatDecimals(priceInMainUnit)
  const lowestPrice = formatStripeAmount(store.lowestPrice, currency, language)
  const lowestPriceAmount = formatDecimals(priceInMainUnit)

  const address = store.gmbAddress ? getGmbAddress(store.gmbAddress) : getAddress(store)
  const firstLiveAtDateTime = store.firstLiveAt ? DateTime.fromISO(store.firstLiveAt.replace(" ", "T")) : DateTime.now()
  const firstLiveAt = firstLiveAtDateTime.toFormat("LLLL yyyy")
  const maxCapacity = store.maxCapacity ? formatCapacity(store.maxCapacity) : unknownCapacityText
  const [firstPoi, secondPoi] = pois ?? []

  return {
    CITY_NAME: city.translatedName,
    COUNTRY_NAME: country,
    FEATURED_POI_NAME: firstPoi?.translatedName ?? store.nickname,
    FIRST_NEARBY_POI: firstPoi?.translatedName ?? "",
    INSURANCE_AMOUNT: bounceGlobals.bounceShieldProtection,
    LOWEST_PRICE_AMOUNT: lowestPriceAmount,
    LOWEST_PRICE: lowestPrice,
    PRICE_AMOUNT: priceAmount,
    PRICE_CURRENCY: currency,
    PRICE: price,
    SECOND_NEARBY_POI: secondPoi?.translatedName ?? "",
    STORE_ADDRESS: address,
    STORE_GMB_NAME: store.gmbLocationName,
    STORE_GOOGLE_MAPS_URL: getGoogleMapsUrl(store),
    STORE_LIVE_DATE: firstLiveAt,
    STORE_LUGGAGE_STORAGE_CAPACITY: maxCapacity,
    STORE_NAME: store.nickname,
    STORE_TYPE: storeType ?? store.type,
    YEAR: new Date().getFullYear().toString(),
  }
}
